import { useState } from 'react';
import { FaGraduationCap, FaTimes, FaWhatsapp } from 'react-icons/fa';
import { SITE_CONFIG } from '../../config/siteConfig';
import { useLeadContext } from '../../context/LeadContext';
import api from '../../api/axios';

const COMMUNITIES = ['OC', 'BC', 'BCM', 'MBC', 'SC', 'SCA', 'ST'];

const GuidanceBanner = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputType, setInputType] = useState<'cutoff' | 'rank'>('cutoff');
  const [form, setForm] = useState({ name: '', phone: '', community: 'OC', cutoff: '', rank: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { collegeName, collegeCode } = useLeadContext();

  const shortCollegeName = (() => {
    if (!collegeName) return '';
    // Preserve Anna University campus identifier (CEG/MIT/ACT)
    const campusMatch = collegeName.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
    if (campusMatch) return `${campusMatch[1].split(',')[0].trim()} — ${campusMatch[2]}`;
    return collegeName.includes(',') ? collegeName.substring(0, collegeName.indexOf(',')).trim() : collegeName;
  })();

  const openModal = () => {
    setSubmitted(false);
    setErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    if (submitted) {
      setForm({ name: '', phone: '', community: 'OC', cutoff: '', rank: '' });
      setSubmitted(false);
    }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!/^[6-9]\d{9}$/.test(form.phone.trim())) e.phone = 'Enter a valid 10-digit mobile number';
    if (inputType === 'cutoff') {
      const v = Number(form.cutoff);
      if (!form.cutoff || isNaN(v) || v < 0 || v > 200) e.value = 'Enter a valid cutoff mark (0–200)';
    } else {
      const v = Number(form.rank);
      if (!form.rank || isNaN(v) || v < 1) e.value = 'Enter a valid rank';
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    try {
      await api.post('/leads', {
        name: form.name.trim(),
        phone: form.phone.trim(),
        community: form.community,
        cutoff_mark: inputType === 'cutoff' ? Number(form.cutoff) : null,
        tnea_rank: inputType === 'rank' ? Number(form.rank) : null,
        college_code: collegeCode || null,
        college_name: collegeName || null,
      });

      const parts = [
        `Hi, I need TNEA ${SITE_CONFIG.year} guidance.`,
        `Name: ${form.name.trim()}`,
        `Mobile: ${form.phone.trim()}`,
        `Community: ${form.community}`,
        inputType === 'cutoff' ? `Cutoff: ${form.cutoff}` : `TNEA Rank: ${form.rank}`,
      ];
      if (collegeName) parts.push(`College of interest: ${collegeName}`);

      const whatsappLink = `https://wa.me/${SITE_CONFIG.whatsappNumber}?text=${encodeURIComponent(parts.join('\n'))}`;
      window.open(whatsappLink, '_blank');
      setSubmitted(true);
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent outline-none ${errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200'}`;

  return (
    <>
      {/* Banner */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-2.5 px-2 md:px-4 text-center shadow-sm sticky top-16 z-40 overflow-x-hidden">
        <button
          onClick={openModal}
          className="inline-flex items-center justify-center gap-1.5 md:gap-2 hover:underline font-medium text-xs md:text-base w-full transition-opacity hover:opacity-90"
        >
          <FaGraduationCap className="hidden md:block text-xl flex-shrink-0" />
          <span className="leading-tight">
            {shortCollegeName
              ? `Considering ${shortCollegeName}? Get free TNEA ${SITE_CONFIG.year} guidance`
              : SITE_CONFIG.bannerText}
          </span>
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center md:p-4"
          onClick={closeModal}
        >
          {/* Bottom sheet on mobile, centered card on desktop */}
          <div
            className="bg-white w-full md:max-w-md md:rounded-xl rounded-t-2xl shadow-2xl relative animate-fadeIn max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle — mobile only */}
            <div className="flex justify-center pt-3 pb-1 md:hidden">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="p-6">
            {/* Close */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <FaTimes className="text-xl" />
            </button>

            {/* Header */}
            <div className="flex justify-center mb-4">
              <div className="bg-primary-100 text-primary-600 w-14 h-14 rounded-full flex items-center justify-center">
                <FaGraduationCap className="text-2xl" />
              </div>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-1">
              {SITE_CONFIG.modalTitle}
            </h2>
            <p className="text-center text-gray-500 text-sm mb-2">
              Expert guidance + personalized college preference list
            </p>
            <div className="text-center mb-5">
              <span className="text-xl font-bold text-primary-600">{SITE_CONFIG.consultationFee}</span>
            </div>

            {submitted ? (
              /* Success state */
              <div className="text-center py-4">
                <div className="text-5xl mb-3">🎉</div>
                <p className="font-semibold text-gray-800 mb-1">WhatsApp opened!</p>
                <p className="text-sm text-gray-500 mb-4">
                  Your details are saved. Our counsellor will reach out shortly on WhatsApp.
                </p>
                <button onClick={closeModal} className="text-sm text-primary-600 hover:underline">
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Name */}
                <div>
                  <input
                    type="text"
                    placeholder="Your name *"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className={inputClass('name')}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    placeholder="Mobile number * (10 digits)"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    className={inputClass('phone')}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                {/* Community */}
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">Community *</p>
                  <div className="flex flex-wrap gap-2">
                    {COMMUNITIES.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, community: c }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          form.community === c
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cutoff / Rank toggle */}
                <div>
                  <div className="flex gap-2 mb-2">
                    {(['cutoff', 'rank'] as const).map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => { setInputType(t); setErrors(e => ({ ...e, value: '' })); }}
                        className={`flex-1 py-2 text-xs rounded-lg border font-medium transition-colors ${inputType === t ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:border-primary-400'}`}
                      >
                        {t === 'cutoff' ? 'Cutoff Mark' : 'TNEA Rank'}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    placeholder={inputType === 'cutoff' ? 'Cutoff mark (0–200) *' : 'Your TNEA rank *'}
                    value={inputType === 'cutoff' ? form.cutoff : form.rank}
                    onChange={e => setForm(f => inputType === 'cutoff'
                      ? { ...f, cutoff: e.target.value }
                      : { ...f, rank: e.target.value }
                    )}
                    min={inputType === 'cutoff' ? 0 : 1}
                    max={inputType === 'cutoff' ? 200 : undefined}
                    step={inputType === 'cutoff' ? 0.5 : 1}
                    className={inputClass('value')}
                  />
                  {errors.value && <p className="text-red-500 text-xs mt-1">{errors.value}</p>}
                </div>

                {/* College auto-filled */}
                {shortCollegeName && (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                    <span className="text-xs text-blue-700 flex-1">📍 {shortCollegeName}</span>
                    <span className="text-[10px] text-blue-400 bg-blue-100 px-1.5 py-0.5 rounded">auto-filled</span>
                  </div>
                )}

                {errors.submit && (
                  <p className="text-red-500 text-xs text-center bg-red-50 rounded-lg py-2">{errors.submit}</p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-colors shadow-md flex items-center justify-center gap-2"
                >
                  <FaWhatsapp className="text-lg" />
                  {submitting ? 'Saving...' : 'Get Guidance on WhatsApp'}
                </button>

                <p className="text-center text-xs text-gray-400">
                  ⚡ Limited slots available • Expert counselors
                </p>
              </div>
            )}
            </div> {/* end p-6 wrapper */}
          </div>
        </div>
      )}
    </>
  );
};

export default GuidanceBanner;
